import { StoreUtils } from '../../utils/util';

export const REPORT_DETAILS = (): {
  [key: string]: {
    name: string;
    description: string;
    url: string;
  }[];
} => ({
  Stock: [
    {
      name: 'Batchwise Stock Report',
      description: 'Detailed view of the current stock status of the inventory.',
      url: '/report/stock-report',
    },
    {
      name: 'Expiry Report',
      description: 'Detailed view of all items which are expired or about to expire.',
      url: '/report/expiry-report',
    },
    {
      name: 'Stock Adjustment',
      description: 'Detailed view of all stocks which are adjusted.',
      url: '/report/stock-adjustment-report',
    },
    {
      name: 'Non Moving Items',
      description: 'Detailed view to understand items which are not sold recently.',
      url: '/report/non-moving-items-report',
    },
    {
      name: 'Item Mapping',
      description: 'Detailed view to understand the item mapping history.',
      url: '/report/item-mapping-report',
    },
    {
      name: 'Low Stock',
      description: 'Detailed view to see which items are below the set threshold.',
      url: '/report/low-stock-report',
    },
    {
      name: 'Item Master Report',
      description: 'Detailed view of the item master.',
      url: '/report/item-master-report',
    },
    {
      name: 'Schedule H/H1/X/G Report',
      description: 'Detailed view of all the Schedule H/H1/X/G drugs sold.',
      url: '/report/schedule-report',
    },
    {
      name: 'New Item Report',
      description: 'Detailed view of all the new items added to the inventory.',
      url: '/report/new-item-report',
    },
    {
      name: 'Itemwise Stock Report',
      description:
        'Detailed view of the current itemwise stock status of the inventory with sales and purchase for a certain time range.',
      url: '/report/itemwise-stock-report',
    },
    {
      name: 'Bounce Item Report',
      description: 'Detailed view of Bounce Item Report.',
      url: '/report/bounce-item-report',
    },
    {
      name: 'Stock Audit Report',
      description: 'Detailed view of Stock Audit Report.',
      url: '/report/stock-audit-report',
    },
    {
      name: 'Opening Stock Report',
      description: 'Detailed view of the opening stock status of the inventory.',
      url: '/report/opening-stock-report',
    },
    ...(StoreUtils.isEnquiryModeActivated()
      ? [
          {
            name: 'Enquiry Report',
            description: 'Detailed view of the enquiries made for items.',
            url: '/report/enquiry-report',
          },
        ]
      : []),
  ],
  Sales: [
    {
      name: 'Oversold Report',
      description: 'Detailed view of all the items which were sold more than they were bought.',
      url: '/report/oversold-report',
    },
    {
      name: 'Itemwise Sales Margin',
      description: 'Detailed itemwise view of the margin generated in each sales.',
      url: '/report/itemwise-sales-margin-report',
    },
    {
      name: 'Batchwise Sales Report',
      description: 'Detailed view of the batchwise information of all items sold',
      url: '/report/batchwise-sales-report',
    },
    {
      name: 'Itemwise Sales Report',
      description: 'Detailed view of the itemwise information of all items sold',
      url: '/report/itemwise-sales-report',
    },
    {
      name: 'Staffwise Sales Report',
      description: 'Detailed view of the staffwise information of all items sold',
      url: '/report/staffwise-sales-report',
    },
    {
      name: 'Salesperson Incentive Report',
      description: 'Detailed view of the incentives earned by the sales person.',
      url: '/report/salesperson-report',
    },
    {
      name: 'Shift Management Report',
      description: 'Detailed view of the Shift information of all Shifts',
      url: '/report/shift-management-report',
    },
  ],
  Purchase: [
    {
      name: 'Batchwise Purchase Report',
      description: 'Detailed view of the batchwise information of all items purchased',
      url: '/report/batchwise-purchase-report',
    },
    {
      name: 'Overbought Report',
      description: 'Detailed view of all the items which were bought more than they were sold.',
      url: '/report/overbought-report',
    },
    {
      name: 'Daily Summary Report',
      description: 'Detailed Daily Summary Report for a certain time range.',
      url: '/report/daily-summary-report',
    },
    ...(StoreUtils.isGRNEnabled()
      ? [
          {
            name: 'GRN History Report',
            description: 'Detailed view of GRN History.',
            url: '/report/grn-history-report',
          },
        ]
      : []),
    {
      name: 'Supplier Due Date Report',
      description: 'Detailed Supplier Due Date Report for a certain time range.',
      url: '/report/supplier-due-date-report',
    },
  ],
  GST: [
    {
      name: 'GSTR Sales',
      description: 'Detailed view with GST breakup of Sales with 2 templates.',
      url: '/report/gst-report-sales',
    },
    {
      name: 'GSTR Purchase',
      description: 'Detailed view with GST breakup of Purchase with 2 templates.',
      url: '/report/gst-report-purchase',
    },
    {
      name: 'GSTR-1 Report',
      description:
        'Detailed view of invoice level or summary level information of the filed GSTR-1s.',
      url: '/report/gst-report-gstr1',
    },
    {
      name: 'GSTR-2A Report',
      description: "Auto-drafted statement that provides ITC details based on the supplier's data.",
      url: '/report/gst-report-gstr2a',
    },
    {
      name: 'GSTR-3B Report',
      description:
        'Simplified summary return to declare the summary GST liabilities for a particular tax period.',
      url: '/report/gst-report-gstr3b',
    },
  ],
  WMS: [
    ...(StoreUtils.isWMSStore()
      ? [
          {
            name: 'Branch PO Report',
            description: 'Detailed view of wms Branch PO.',
            url: '/report/branch-po-report',
          },
          {
            name: 'GRN History Report',
            description: 'Detailed view of GRN History.',
            url: '/report/grn-history-report',
          },
        ]
      : []),
  ],
  'WMS B2C': [
    ...(StoreUtils.isWmsB2cStore()
      ? [
          {
            name: 'FKH Report',
            description: 'Detailed view of FKH Report.',
            url: '/report/fkh-report',
          },
        ]
      : []),
  ],
  Tally: [
    ...(StoreUtils.getKeyFromActiveSession(['config', 'tallyIntegration'])
      ? [
          {
            name: 'Tally Master',
            description: 'Detailed view of Tally master with XML.',
            url: '/report/tally-master',
          },
          {
            name: 'Tally Transaction',
            description: 'Detailed view of Tally Transaction with XML.',
            url: '/report/tally-transaction',
          },
        ]
      : []),
  ],
});

export const REPORT_STATUS = {
  generated: 'Generated',
  pending: 'Pending',
  failed: 'Failed',
  synced: 'Synced',
};

export const moveColToFirst = (col: string[], name: string): string[] => {
  if (col.includes(name)) {
    return [name, ...col.filter((item) => item !== name)];
  }
  return col;
};

export const typePathMap: Record<string, string> = {
  Supplier: '/suppliers',
  Retailer: '/wholesale/retailers',
  Sales: '/bill-history',
  'Sales Return': '/sales-return-history',
  Purchase: '/purchase-history',
  'Purchase Return': '/purchase-return-history',
  Wholesale: '/wholesale/bill-history',
  'Wholesale Return': '/wholesale-return-history',
};

export const syncStatusMap: Record<string, { key: string; label: string; color: string }> = {
  pending: { key: 'pending', label: 'Pending', color: '#FF5733' }, // Orange-Red
  inProgress: { key: 'inProgress', label: 'In Progress', color: '#FF8C00' }, // Dark Orange
  partial: { key: 'partial', label: 'Partial', color: '#FFD700' }, // Gold
  done: { key: 'done', label: 'Done', color: '#4CAF50' }, // Green
  notApplicable: { key: 'notApplicable', label: 'Not Applicable', color: '#A9A9A9' }, // Dark Gray
  failed: { key: 'failed', label: 'Failed', color: '#B22222' }, // Firebrick Red
};

const reports = ['Tally', 'WMS', 'WMS B2C'];

export const getStoreCheck = (dataValue: string) => {
  switch (dataValue) {
    case 'WMS':
      return StoreUtils.isWMSStore();
    case 'WMS B2C':
      return StoreUtils.isWmsB2cStore();
    case 'Tally':
      return StoreUtils.getKeyFromActiveSession(['config', 'tallyIntegration']);
    default:
      return !reports.includes(dataValue);
  }
};
